import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../api/profiles';
import { buildProfilePath } from '../utils/profileUrl';
import { useNotifications } from '../contexts/NotificationsContext';
import { Search as SearchIcon, User, LogOut, Settings, Menu, X, Shield, Bell, Home, LayoutGrid, Grid3X3, UserCircle2, MessageSquare, Sun, Moon, CalendarDays, Newspaper } from 'lucide-react';
import SearchSpotlight from './SearchSpotlight';
// import { getAvatarUrl } from '../utils/imageUtils';
// import LazyImage from './LazyImage';
import VerifiedAvatar from './VerifiedAvatar';
//import { HeaderCallButton } from './phone/HeaderCallButton';
import { BRAND } from '../config/brand';
import { useTheme } from '../hooks/useTheme';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  // Build canonical profile link for the current user.
  // Falls back to legacy /user/:id while loading.
  const { data: myProfileData } = useProfile(user?.id);
  const myProfileLink = user
    ? buildProfilePath({
      profile: myProfileData?.profile || { id: user.id, username: user.username },
      categoryPath: myProfileData?.categoryPath
    })
    : '/login';

  const accountType = String(user?.account_type || 'profile');
  const isGuest = accountType === 'guest';

  // existing menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // searchQuery removed (SearchSpotlight is used instead)

  // Mobile sticky icons behavior (only on mobile < lg)
  // When main nav is visible -> icon bar sits below it (top-16)
  // When main nav scrolled out -> icon bar sticks to top-0
  const [showMobileNav, setShowMobileNav] = useState(true);
  const lastScrollY = useRef(0);

  // Mobile: hide navbar on scroll down, show on scroll up
  useEffect(() => {
    const onScroll = () => {
      if (typeof window === 'undefined') return;
      if (window.innerWidth >= 1024) {
        setShowMobileNav(true);
        lastScrollY.current = window.scrollY || 0;
        return;
      }
      const y = window.scrollY || 0;
      const delta = y - (lastScrollY.current || 0);
      lastScrollY.current = y;

      if (y < 10) { setShowMobileNav(true); return; }
      if (Math.abs(delta) < 4) return; // ignore tiny jitters

      if (delta > 0) {
        // scrolling down -> hide
        setShowMobileNav(false);
      } else {
        // scrolling up -> show
        setShowMobileNav(true);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // search spotlight state
  const [searchOpen, setSearchOpen] = useState(false);

  // Theme (light/dark) via Tailwind `dark` class
  const { theme, toggleTheme } = useTheme();

  // Logo text flip is handled by CSS 3D faces (no JS timers needed)

  // const handleSearch = (e) => {
  //   e.preventDefault();
  //   if (searchQuery.trim()) {
  //     navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  //     setSearchQuery('');
  //   }
  // };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Derived visibility for mobile: keep shown when menu or search is open
  const mobileNavVisible = isMobileMenuOpen || searchOpen || showMobileNav;

  // Desktop user dropdown: open/close on hover + close on outside click (touch-friendly fallback)
  const userMenuRef = useRef(null);
  const userMenuCloseTimerRef = useRef(null);

  const cancelUserMenuClose = useCallback(() => {
    if (userMenuCloseTimerRef.current) {
      try { clearTimeout(userMenuCloseTimerRef.current); } catch (_) { }
      userMenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleUserMenuClose = useCallback(() => {
    cancelUserMenuClose();
    // Small delay so the cursor can travel from avatar -> dropdown
    userMenuCloseTimerRef.current = setTimeout(() => {
      setIsMenuOpen(false);
    }, 220);
  }, [cancelUserMenuClose]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const onDocPointerDown = (e) => {
      try {
        if (!userMenuRef.current) return;
        if (userMenuRef.current.contains(e.target)) return;
        setIsMenuOpen(false);
      } catch (_) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('touchstart', onDocPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('touchstart', onDocPointerDown);
    };
  }, [isMenuOpen]);

  return (
    <nav className={`border-white/40 bg-white/80 dark:bg-secondary-900/80 backdrop-blur-md shadow-soft border-b border-secondary-200 dark:border-secondary-700 sticky top-0 z-50 transform-gpu transition-transform duration-300 will-change-transform ${mobileNavVisible ? 'translate-y-0' : '-translate-y-full'} lg:translate-y-0`}>
      <style>{`
        :root { --safe-top: env(safe-area-inset-top, 0px); }
        /* 3D X-axis rotation; front visible 0-179°, back 180-359° */
        @keyframes logo-rotate-y {
          0%   { transform: rotateX(0deg); }
          50%  { transform: rotateX(180deg); }
          100% { transform: rotateX(360deg); }
        }
        /* Explicit text swap to avoid backface glitches in Firefox */
        @keyframes logo-text-front {
          0%, 49.9% { opacity: 1; }
          50%, 100% { opacity: 1; }
        }
        @keyframes logo-text-back {
          0%, 49.9% { opacity: 1; }
          50%, 100% { opacity: 1; }
        }

        /* Desktop-only: user-pill reveals username on hover */
        @media (min-width: 1024px) {
          .user-pill {
            overflow: hidden;
            /* allow smooth width/padding transitions */
            transition: background-color .2s ease, transform .2s ease;
            will-change: width, background-color;
          }
          .user-pill__label {
            display: inline-block;
            white-space: nowrap;
            max-width: 0;
            opacity: 0;
            transform: translateX(-6px);
            margin-left: 0;
            transition: max-width .35s ease, opacity .25s ease, transform .35s ease, margin-left .25s ease;
          }
          .user-pill:hover .user-pill__label {
            max-width: 200px; /* enough for long names */
            opacity: 1;
            transform: translateX(0);
            margin-left: .5rem; /* visual gap from avatar only when revealed */
          }
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo + Desktop primary nav */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center space-x-2" style={{ perspective: '600px' }}>
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">a</span>
              </div>
              <span
                className="relative inline-block text-xl font-bold width-logo"
                style={{ lineHeight: 1 }}
              >
                <span
                  className="block transform-gpu"
                  style={{
                    animation: 'logo-rotate-y 20s cubic-bezier(1, -0.2, 0, 1.19) infinite',
                    transformStyle: 'preserve-3d',
                    willChange: 'transform',
                    transformOrigin: '50% 50%',
                    position: 'relative',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden'
                  }}
                >
                  <span
                    className="block"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateX(0deg) translateZ(0.001px)',
                      animation: 'logo-text-front 6s linear infinite'
                    }}
                  >
                    <span className="text-secondary-900">{BRAND.logo_1}</span>
                    <span className="text-primary-600">{BRAND.logo_2}</span>
                    <span className="text-secondary-600">{BRAND.logo_3}</span>
                  </span>
                  <span
                    className="block absolute top-0 left-0 font-10"
                    style={{
                      transform: 'rotateX(180deg) translateZ(0.001px)',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      animation: 'logo-text-back 6s linear infinite'
                    }}
                  >
                    {BRAND.logoRu}
                  </span>
                </span>
              </span>
              <span className="beta">beta</span>
            </Link>

            {/* Desktop primary nav (moved into top bar) */}
            <div className="hidden lg:flex items-center gap-2">
              <Link
                to="/"
                className={`group rounded-xl px-3 py-2 inline-flex items-center ${location.pathname === '/' ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <Home className={`w-5 h-5 ${location.pathname === '/' ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="ml-2 text-sm text-secondary-800">Главная</span>
              </Link>

              <Link
                to="/feed"
                className={`group rounded-xl px-3 py-2 inline-flex items-center ${location.pathname.startsWith('/feed') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <LayoutGrid className={`w-5 h-5 ${location.pathname.startsWith('/feed') ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="ml-2 text-sm text-secondary-800">Лента</span>
              </Link>

              <Link
                to="/events"
                className={`group rounded-xl px-3 py-2 inline-flex items-center ${location.pathname.startsWith('/events') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <CalendarDays className={`w-5 h-5 ${location.pathname.startsWith('/events') ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="ml-2 text-sm text-secondary-800">События</span>
              </Link>

              <Link
                to="/katalog"
                className={`group rounded-xl px-3 py-2 inline-flex items-center ${location.pathname.startsWith('/katalog') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <Grid3X3 className={`w-5 h-5 ${location.pathname.startsWith('/katalog') ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="ml-2 text-sm text-secondary-800">Каталог</span>
              </Link>

              <Link
                to={user ? (isGuest ? '/account' : myProfileLink) : '/login'}
                className={`group rounded-xl px-3 py-2 inline-flex items-center ${location.pathname.startsWith('/user') || location.pathname.startsWith('/settings') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <User className={`w-5 h-5 ${location.pathname.startsWith('/user') || location.pathname.startsWith('/settings') ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="ml-2 text-sm text-secondary-800">{isGuest ? 'Аккаунт' : 'Профиль'}</span>
              </Link>
            </div>
          </div>




          {/* Desktop Navigation Items */}
          <div className="hidden lg:flex items-center space-x-8">
            {user ? (
              <>
                <div className="flex items-center gap-2">
                  {/* Search */}
                  <button
                    onClick={() => setSearchOpen(true)}
                    title="Поиск"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-secondary-700 border border-white/40 hover:bg-secondary-50 shadow-sm active:scale-95 transition dark:bg-secondary-800 dark:text-secondary-100 dark:border-secondary-600 dark:hover:bg-secondary-700"
                  >
                    <SearchIcon className="w-5 h-5 text-secondary-700" />
                  </button>

                  {/* Notifications (icon-only) */}
                  <Link
                    to="/notifications"
                    title="Алёрты"
                    className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-secondary-700 border border-white/40 hover:bg-secondary-50 shadow-sm active:scale-95 transition dark:bg-secondary-800 dark:text-secondary-100 dark:border-secondary-600 dark:hover:bg-secondary-700"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold text-white bg-red-600 rounded-full">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>

                  {/* Theme toggle */}
                  <button
                    onClick={toggleTheme}
                    title={theme === 'dark' ? 'Яркая тема' : 'Темная тема'}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-secondary-700 border border-white/40 hover:bg-secondary-50 shadow-sm active:scale-95 transition dark:bg-secondary-800 dark:text-secondary-100 dark:border-secondary-600 dark:hover:bg-secondary-700"
                  >
                    {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </button>

                  {/* Legacy create pin/board actions removed (profiles-only) */}

                  {/* {isAdmin && (
                    <span title="Звонок (только Admin/Superadmin)">
                      <HeaderCallButton />
                    </span>
                  )} */}
                </div>

                {/* Notifications dropdown in Navbar intentionally removed (use /notifications page). */}

                {/* User Menu */}
                <div
                  className="relative"
                  ref={userMenuRef}
                  onMouseEnter={() => {
                    cancelUserMenuClose();
                    setIsMenuOpen(true);
                  }}
                  onMouseLeave={() => {
                    scheduleUserMenuClose();
                  }}
                >
                  <button
                    onClick={() => {
                      cancelUserMenuClose();
                      setIsMenuOpen(true);
                    }}
                    className="user-pill flex items-center p-2 rounded-full hover:bg-secondary-100 transition-all duration-300"
                  >
                    <VerifiedAvatar
                      user={user}
                      src={user.avatar_url}
                      alt={user.username}
                      size={32}
                      rounded="full"
                      className=""
                    />
                    <span className="user-pill__label text-secondary-700 font-medium">{user.username}</span>
                  </button>

                  {isMenuOpen && (
                    <div
                      className="absolute right-0 z-50 mt-2 w-48 bg-white rounded-lg shadow-medium border border-secondary-200 py-2"
                      onMouseEnter={() => {
                        cancelUserMenuClose();
                        setIsMenuOpen(true);
                      }}
                      onMouseLeave={() => {
                        scheduleUserMenuClose();
                      }}
                    >

                      <Link
                        to="/messages"
                        className="flex items-center space-x-2 px-4 py-2 text-secondary-800 hover:bg-secondary-50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>Сообщения</span>
                      </Link>

                      {!isGuest && (
                        <Link
                          to="/settings"
                          className="flex items-center space-x-2 px-4 py-2 text-secondary-800 hover:bg-secondary-50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4" />
                          <span>Арт-профиль</span>
                        </Link>
                      )}

                      <Link
                        to="/account"
                        className="flex items-center space-x-2 px-4 py-2 text-secondary-800 hover:bg-secondary-50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <UserCircle2 className="w-4 h-4" />
                        <span>Аккаунт</span>
                      </Link>

                      <Link
                        to="/my-events"
                        className="flex items-center space-x-2 px-4 py-2 text-secondary-800 hover:bg-secondary-50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <CalendarDays className="w-4 h-4" />
                        <span>Мои события</span>
                      </Link>

                      <Link
                        to="/my-posts"
                        className="flex items-center space-x-2 px-4 py-2 text-secondary-800 hover:bg-secondary-50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Newspaper className="w-4 h-4" />
                        <span>Мои посты</span>
                      </Link>

                      {isAdmin && (
                        <>

                          <Link
                            to="/admin"
                            className="flex items-center space-x-2 px-4 py-2 text-purple-700 hover:bg-purple-50"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Shield className="w-4 h-4" />
                            <span>Админ</span>
                          </Link>
                        </>
                      )}


                      <div className="border-t border-secondary-200 my-1"></div>
                      <button
                        onClick={handleLogout}
                        title="Выйти"
                        className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Выйти</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (

              <div className="flex items-center space-x-4">
                {/* Search */}
                <button
                  onClick={() => setSearchOpen(true)}
                  title="Поиск"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-secondary-700 border border-white/40 hover:bg-secondary-50 shadow-sm active:scale-95 transition dark:bg-secondary-800 dark:text-secondary-100 dark:border-secondary-600 dark:hover:bg-secondary-700"
                >
                  <SearchIcon className="w-5 h-5 text-secondary-700" />
                </button>
                <button
                  onClick={toggleTheme}
                  title={theme === 'dark' ? 'Яркая тема' : 'Темная тема'}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-secondary-700 border border-white/40 hover:bg-secondary-50 shadow-sm active:scale-95 transition dark:bg-secondary-800 dark:text-secondary-100 dark:border-secondary-600 dark:hover:bg-secondary-700"
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <Link to="/login" className="btn btn-ghost">
                  Войти
                </Link>
                <Link to="/register" className="btn btn-primary">
                  Регистрация
                </Link>
              </div>
            )}
          </div>

          {/* Mobile actions (inline, like the mock) */}
          <div className="lg:hidden flex items-center space-x-2">
            <button
              onClick={() => setSearchOpen(true)}
              title="Поиск"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/70 text-secondary-700 border border-white/40 hover:bg-white shadow-sm active:scale-95 transition dark:bg-secondary-800 dark:text-secondary-100 dark:border-secondary-600 dark:hover:bg-secondary-700"
            >
              <SearchIcon className="w-5 h-5" />
            </button>

            {/* Notifications (icon-only) */}
            <Link
              to="/notifications"
              title="Алёрты"
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/70 text-secondary-700 border border-white/40 hover:bg-white shadow-sm active:scale-95 transition dark:bg-secondary-800 dark:text-secondary-100 dark:border-secondary-600 dark:hover:bg-secondary-700"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold text-white bg-red-600 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>

            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Яркая тема' : 'Темная тема'}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/70 text-secondary-700 border border-white/40 hover:bg-white shadow-sm active:scale-95 transition dark:bg-secondary-800 dark:text-secondary-100 dark:border-secondary-600 dark:hover:bg-secondary-700"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            {/* Legacy create pin/board actions removed (profiles-only) */}
            {/* {user && isAdmin && <HeaderCallButton />} */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              title="Меню"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-secondary-700 hover:bg-secondary-100 shadow-sm active:scale-95 transition"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>


        {/* Desktop segmented navigation (under header, similar to mobile but horizontal) */}
        <div className="hidden">
          <div className="rounded-2xl border border-white/40 bg-white/80 backdrop-blur-md shadow-soft p-2">
            <div className="flex items-center justify-between gap-2">
              {/* Left: segments */}
              <div className="flex items-center gap-2">
                <Link
                  to="/"
                  className={`group rounded-xl px-3 py-2 inline-flex items-center ${location.pathname === '/' ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
                >
                  <Home className={`w-5 h-5 ${location.pathname === '/' ? 'text-primary-600' : 'text-secondary-700'}`} />
                  <span className="ml-2 text-sm text-secondary-800">Главное</span>
                </Link>

                <Link
                  to={user ? myProfileLink : '/login'}
                  className={`group rounded-xl px-3 py-2 inline-flex items-center ${location.pathname.startsWith('/user') || location.pathname.startsWith('/settings') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
                >
                  <User className={`w-5 h-5 ${location.pathname.startsWith('/user') || location.pathname.startsWith('/settings') ? 'text-primary-600' : 'text-secondary-700'}`} />
                  <span className="ml-2 text-sm text-secondary-800">Профиль</span>
                </Link>

                <Link
                  to="/notifications"
                  className={`group rounded-xl px-3 py-2 inline-flex items-center relative ${location.pathname.startsWith('/notifications') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
                >
                  <Bell className={`w-5 h-5 ${location.pathname.startsWith('/notifications') ? 'text-primary-600' : 'text-secondary-700'}`} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold text-white bg-red-600 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              </div>

              {/* Right: icon cluster */}
              {user && (
                <div className="flex items-center gap-2">
                  {/* Search */}
                  <button
                    onClick={() => setSearchOpen(true)}
                    title="Поиск"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-secondary-700 border border-white/40 hover:bg-secondary-50 shadow-sm active:scale-95 transition"
                  >
                    <SearchIcon className="w-5 h-5 text-secondary-700" />
                  </button>

                  {/* Legacy create pin/board actions removed (profiles-only) */}

                  {/* Settings */}
                  <Link
                    to="/settings"
                    title="Настройки"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-secondary-700 border border-white/40 hover:bg-secondary-50 shadow-sm active:scale-95 transition"
                  >
                    <Settings className="w-5 h-5" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile segmented navigation (under header) */}
        <div className="lg:hidden mb-3">
          <div className="rounded-2xl border border-secondary-200 bg-white shadow-soft p-2">
            <div className="grid grid-cols-5 gap-2">
              <Link
                to="/"
                className={`group rounded-xl py-2 flex flex-col items-center ${location.pathname === '/' ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <Home className={`w-6 h-6 ${location.pathname === '/' ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="mt-1 text-xs text-secondary-800">Главное</span>
              </Link>

              <Link
                to="/feed"
                className={`group rounded-xl py-2 flex flex-col items-center ${location.pathname.startsWith('/feed') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <LayoutGrid className={`w-6 h-6 ${location.pathname.startsWith('/feed') ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="mt-1 text-xs text-secondary-800">Лента</span>
              </Link>

              <Link
                to={user ? (isGuest ? '/account' : myProfileLink) : '/login'}
                className={`group rounded-xl py-2 flex flex-col items-center ${location.pathname.startsWith('/user') || location.pathname.startsWith('/settings') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <User className={`w-6 h-6 ${location.pathname.startsWith('/user') || location.pathname.startsWith('/settings') ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="mt-1 text-xs text-secondary-800">Профиль</span>
              </Link>

              <Link
                to="/events"
                className={`group rounded-xl py-2 flex flex-col items-center ${location.pathname.startsWith('/events') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <CalendarDays className={`w-6 h-6 ${location.pathname.startsWith('/events') ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="mt-1 text-xs text-secondary-800">События</span>
              </Link>

              <Link
                to="/katalog"
                className={`group rounded-xl py-2 flex flex-col items-center ${location.pathname.startsWith('/katalog') ? 'bg-secondary-50' : 'hover:bg-secondary-50'}`}
              >
                <Grid3X3 className={`w-6 h-6 ${location.pathname.startsWith('/katalog') ? 'text-primary-600' : 'text-secondary-700'}`} />
                <span className="mt-1 text-xs text-secondary-800">Каталог</span>
              </Link>

            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4">

            {/* Mobile Navigation Items */}
            {user ? (
              <div className="space-y-2">

                {/* Mobile User Menu */}
                <div className="pt-4">
                  <div className="flex items-center space-x-3 px-4 py-3">
                    <VerifiedAvatar
                      user={user}
                      src={user.avatar_url}
                      alt={user.username}
                      sizeClass="w-10 h-10"
                      rounded="full"
                    />
                    <div>
                      <div className="font-medium text-secondary-900">{user.username}</div>
                      <div className="text-sm text-secondary-600">Пользователь</div>
                    </div>
                  </div>


                  <Link
                    to="/messages"
                    className="flex items-center space-x-3 px-4 py-3 text-secondary-800 hover:bg-secondary-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <MessageSquare className="w-5 h-5" />
                    <span>Сообщения</span>
                  </Link>

                  {!isGuest && (
                    <Link
                      to="/settings"
                      className="flex items-center space-x-3 px-4 py-3 text-secondary-800 hover:bg-secondary-50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Settings className="w-5 h-5" />
                      <span>Настройки</span>
                    </Link>
                  )}

                  <Link
                    to="/account"
                    className="flex items-center space-x-3 px-4 py-3 text-secondary-800 hover:bg-secondary-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <UserCircle2 className="w-5 h-5" />
                    <span>Аккаунт</span>
                  </Link>

                  <Link
                    to="/my-events"
                    className="flex items-center space-x-3 px-4 py-3 text-secondary-800 hover:bg-secondary-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <CalendarDays className="w-5 h-5" />
                    <span>Мои события</span>
                  </Link>

                  <Link
                    to="/my-posts"
                    className="flex items-center space-x-3 px-4 py-3 text-secondary-800 hover:bg-secondary-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Newspaper className="w-5 h-5" />
                    <span>Мои посты</span>
                  </Link>


                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center space-x-3 px-4 py-3 text-purple-700 hover:bg-purple-50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Shield className="w-5 h-5" />
                      <span>Администрирование</span>
                    </Link>
                  )}

                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 w-full text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Выйти</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block w-full text-center py-3 px-4 border border-secondary-200 rounded-lg text-secondary-700 hover:bg-secondary-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Войти
                </Link>
                <Link
                  to="/register"
                  className="block w-full text-center py-3 px-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Регистрация
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
      <SearchSpotlight
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </nav>




  );
};

export default Navbar;
